import React, { useMemo } from "react"
import {
  Box, Flex, Stack, Text,
} from "@chakra-ui/react"
import Button from "components/Buttons/Button"
import Icon from "components/elements/Icon"
import OptInMsgIcon from "components/elements/OptInMsgIcon"
import useModal from "services/useModal"
import { useTextMessages } from "queries/texts"
import useDefaultOptInText from "services/useDefaultOptInText"
import useOptInContentSids from "services/useOptInContentSids"
import { DioboxEvent, ModalName } from "sharedTypes"

type Props = {
  event: DioboxEvent
}

type AutoOptInState = "enabled" | "disabled" | "not_configured"
type FallbackState = "enabled" | "disabled"

const ICON_COLOR: Record<AutoOptInState, string> = {
  enabled: "blue.500",
  disabled: "red.500",
  not_configured: "orange.500",
}

const LABEL_COLOR: Record<AutoOptInState, string> = {
  enabled: "green.500",
  disabled: "red.500",
  not_configured: "orange.500",
}

const STATE_LABEL: Record<AutoOptInState, string> = {
  enabled: "Enabled",
  disabled: "Disabled",
  not_configured: "Not Configured",
}

const OptionsRow = ({ event }: Props) => {
  const showModal = useModal()
  const { data: textsResponse } = useTextMessages(event.id)
  const texts = textsResponse?.data ?? []
  const optInContentSids = useOptInContentSids()

  const autoOptInEnabled = event.autoOptInEnabled ?? true
  const smsFallbackEnabled = event.smsFallbackEnabled ?? true
  const { isConfigured: isDefaultOptInConfigured } = useDefaultOptInText(
    event.id,
    event.defaultOptInTextId,
    event.defaultOptInTextId !== null,
  )
  // "Has an opt-in text" for state purposes requires the saved TextMessage's
  // variables to all be filled — a misconfigured default would fail the BE
  // auto-dispatch, so we surface it as Not Configured rather than Enabled.
  const eventHasUsableOptInText = event.defaultOptInTextId !== null && isDefaultOptInConfigured
  const isDefaultOptInMisconfigured = event.defaultOptInTextId !== null && !isDefaultOptInConfigured

  const autoOptInState: AutoOptInState = !autoOptInEnabled
    ? "disabled"
    : !eventHasUsableOptInText
      ? "not_configured"
      : "enabled"

  const fallbackState: FallbackState = smsFallbackEnabled ? "enabled" : "disabled"

  // If the event already has any opt-in Text but none is set as default,
  // hide the "Add Opt-In Message" button — the user just needs to mark one
  // of the existing texts as default rather than creating a duplicate.
  const hasExistingOptInText = useMemo(
    () => texts.some(
      (t) => t.whatsappContentSid !== undefined && optInContentSids.has(t.whatsappContentSid),
    ),
    [optInContentSids, texts],
  )

  // Default opt-in text's name, used in the Auto Opt-In Enabled
  // description per COMPLIANCE.md §10.4 / §10.11.
  const defaultOptInName = useMemo(() => {
    if (event.defaultOptInTextId === null) return ""

    return texts.find((t) => t.id === event.defaultOptInTextId)?.name ?? ""
  }, [event.defaultOptInTextId, texts])

  const handleAddOptInMessage = () => {
    showModal(ModalName.NewTextMessage, {
      channel: "whatsapp",
      initialMessageType: "opt_in",
    })
  }

  return (
    <Flex alignItems="flex-start">
      <Text fontWeight="bold" w="25%" pt={1}>Options</Text>
      <Box flex={1}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => showModal(ModalName.WhatsAppSendingOptions)}
        >
          Edit Event Sending Options
        </Button>

        <Stack spacing={4} mt={4}>
          <OptionItem
            icon={<OptInMsgIcon boxSize={6} color={ICON_COLOR[autoOptInState]} />}
            title="Auto Opt-In"
            stateLabel={STATE_LABEL[autoOptInState]}
            stateLabelColor={LABEL_COLOR[autoOptInState]}
            description={(
              <AutoOptInDescription
                state={autoOptInState}
                hasExistingOptInText={hasExistingOptInText}
                defaultOptInName={defaultOptInName}
                isDefaultMisconfigured={isDefaultOptInMisconfigured}
                onAddOptIn={handleAddOptInMessage}
              />
            )}
          />
          <OptionItem
            icon={(
              <Icon
                icon="texts-nav"
                size={6}
                color={ICON_COLOR[fallbackState]}
              />
            )}
            title="Fallback to SMS"
            stateLabel={STATE_LABEL[fallbackState]}
            stateLabelColor={LABEL_COLOR[fallbackState]}
            description={(
              <Text>
                {fallbackState === "enabled"
                  ? "If a WhatsApp message cannot be delivered, resend it via SMS."
                  : "If a WhatsApp message cannot be delivered, it won't be tried again via SMS."}
              </Text>
            )}
          />
        </Stack>
      </Box>
    </Flex>
  )
}

type OptionItemProps = {
  icon: React.ReactNode
  title: string
  stateLabel: string
  stateLabelColor: string
  description: React.ReactNode
}

const OptionItem = ({
  icon, title, stateLabel, stateLabelColor, description,
}: OptionItemProps) => (
  <Box>
    <Flex alignItems="center" gap={3}>
      <Box flexShrink={0}>{icon}</Box>
      <Flex alignItems="center" gap={2}>
        <Text fontWeight="medium">{title}</Text>
        <Text fontSize="sm" color={stateLabelColor} fontWeight="medium">
          {stateLabel}
        </Text>
      </Flex>
    </Flex>
    <Box ml="36px" mt={1}>{description}</Box>
  </Box>
)

type AutoOptInDescriptionProps = {
  state: AutoOptInState
  hasExistingOptInText: boolean
  defaultOptInName: string
  isDefaultMisconfigured: boolean
  onAddOptIn: () => void
}

const AutoOptInDescription = ({
  state, hasExistingOptInText, defaultOptInName, isDefaultMisconfigured, onAddOptIn,
}: AutoOptInDescriptionProps) => {
  if (state === "enabled") {
    if (!defaultOptInName) {
      return (
        <Text>
          Automatically send the opt-in message to guests who haven&apos;t opted
          in yet.
        </Text>
      )
    }
    return (
      <Text>
        {"Automatically send the "}
        <Text as="span" fontWeight="medium">{`"${defaultOptInName}"`}</Text>
        {" message to guests who haven't opted in yet."}
      </Text>
    )
  }

  if (state === "not_configured") {
    if (isDefaultMisconfigured) {
      return (
        <Text>
          {"The default opt-in message "}
          {defaultOptInName && (
            <>
              <Text as="span" fontWeight="medium">{`"${defaultOptInName}"`}</Text>
              {" "}
            </>
          )}
          is missing required fields. Open it and fill in the variables to
          enable Auto Opt-In.
        </Text>
      )
    }
    if (hasExistingOptInText) {
      return (
        <Text>
          Set one of your existing opt-in messages as the default to enable
          Auto Opt-In.
        </Text>
      )
    }
    return (
      <Box>
        <Text>
          Set up an opt-in message to automatically send it to guests who
          haven&apos;t opted in yet.
        </Text>
        <Button
          variant="outline"
          size="xs"
          mt={2}
          borderRadius="full"
          px={4}
          onClick={onAddOptIn}
        >
          Add Opt-In Message
        </Button>
      </Box>
    )
  }

  return (
    <Text>
      WhatsApp messages will only be sent to guests who have already opted in.
      To gather consent from other guests, use a registration form or send
      the opt-in message to individual guests from the guest options menu.
    </Text>
  )
}

export default OptionsRow
